import { useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { Tabs } from "@/components/ui/Tabs";
import { Badge, type BadgeTone } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { DataTable } from "@/components/tables/DataTable";
import { toast } from "@/store/toast.store";
import {
  useApprovePayout,
  useMarkPayoutPaid,
  usePayouts,
  useRejectPayout,
} from "@/features/commission/usePayouts";
import { formatINR } from "@/utils/format";
import type { PayoutRow, PayoutStatus } from "@/types/domain";

const TABS = [
  { value: "PENDING", label: "Requested" },
  { value: "PROCESSING", label: "Sending" },
  { value: "PAID", label: "Paid" },
  { value: "FAILED", label: "Failed" },
];

const TONE: Record<PayoutStatus, BadgeTone> = { PENDING: "gold", PROCESSING: "neutral", PAID: "teal", FAILED: "danger" };
const LABEL: Record<PayoutStatus, string> = { PENDING: "Requested", PROCESSING: "Sending", PAID: "Paid", FAILED: "Failed" };

const errorMessage = (e: unknown) =>
  (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ??
  (e as Error)?.message ??
  "Something went wrong";

/** Driver withdrawals: admin approves (money is sent through the provider) or rejects. */
export function PayoutsCard() {
  const [status, setStatus] = useState<PayoutStatus>("PENDING");
  const payouts = usePayouts(status);
  const approve = useApprovePayout();
  const reject = useRejectPayout();
  const markPaid = useMarkPayoutPaid();
  const busy = approve.isPending || reject.isPending || markPaid.isPending;

  const onApprove = (p: PayoutRow) => {
    const warning = !p.upiVerified
      ? `

WARNING: this UPI ID is ${p.upiNameMatch === "REVIEW" ? `verified as "${p.upiHolderName ?? "unknown"}", which does not match the driver's name` : "not verified"}. Check it before paying.`
      : "";
    if (!window.confirm(`Send ${formatINR(p.amount)} to ${p.driverName ?? "the driver"} (${p.upiId ?? "no UPI ID"})?${warning}`)) return;
    approve.mutate(p.id, {
      onSuccess: (r) => toast.success((r as PayoutRow).status === "PAID" ? "Payout sent and confirmed" : "Payout sent, waiting for confirmation"),
      onError: (e) => toast.error(errorMessage(e)),
    });
  };
  const onReject = (p: PayoutRow) => {
    const reason = window.prompt("Reason for rejecting (the driver will see it):");
    if (!reason || reason.trim().length < 3) return;
    reject.mutate(
      { id: p.id, reason: reason.trim() },
      {
        onSuccess: () => toast.success("Payout rejected, amount returned to the driver"),
        onError: (e) => toast.error(errorMessage(e)),
      },
    );
  };
  const onMarkPaid = (p: PayoutRow) => {
    const reference = window.prompt("Reference number of the transfer you made by hand (UTR):");
    if (!reference || !reference.trim()) return;
    markPaid.mutate(
      { id: p.id, reference: reference.trim() },
      {
        onSuccess: () => toast.success("Marked as paid"),
        onError: (e) => toast.error(errorMessage(e)),
      },
    );
  };

  const columns = useMemo<ColumnDef<PayoutRow, unknown>[]>(
    () => [
      { header: "Driver", accessorFn: (p) => p.driverName ?? p.driverId },
      {
        header: "UPI ID",
        id: "upi",
        cell: ({ row: { original: p } }) => (
          <div className="flex flex-col gap-0.5">
            <span>{p.upiId ?? "-"}</span>
            {p.upiId && (
              <Badge tone={p.upiVerified ? "teal" : "gold"}>
                {p.upiVerified ? "Verified" : p.upiNameMatch === "REVIEW" ? "Name differs" : "Not verified"}
              </Badge>
            )}
            {p.upiHolderName && <span className="text-xs text-ink-faint">Bank name: {p.upiHolderName}</span>}
          </div>
        ),
      },
      { header: "Amount", accessorFn: (p) => formatINR(p.amount) },
      { header: "Requested", accessorFn: (p) => new Date(p.createdAt).toLocaleString("en-IN") },
      {
        header: "Status",
        id: "status",
        cell: ({ row: { original: p } }) => (
          <div className="flex flex-col gap-0.5">
            <Badge tone={TONE[p.status]}>{p.rejectedAt ? "Rejected" : LABEL[p.status]}</Badge>
            {p.status === "PAID" && p.reference && <span className="text-xs text-ink-faint">Ref {p.reference}</span>}
            {p.status !== "PAID" && p.failureReason && <span className="text-xs text-danger-500">{p.failureReason}</span>}
          </div>
        ),
      },
      {
        header: "",
        id: "actions",
        cell: ({ row: { original: p } }) => {
          const retryable = p.status === "FAILED" && !p.rejectedAt;
          return (
            <div className="flex flex-wrap justify-end gap-2">
              {(p.status === "PENDING" || retryable) && (
                <Button size="sm" variant="primary" disabled={busy} onClick={() => onApprove(p)}>
                  {retryable ? "Retry" : "Approve & pay"}
                </Button>
              )}
              {(p.status === "PENDING" || retryable) && (
                <Button size="sm" variant="outline" disabled={busy} onClick={() => onReject(p)}>
                  Reject
                </Button>
              )}
              {(p.status === "PENDING" || p.status === "PROCESSING" || retryable) && (
                <Button size="sm" variant="ghost" disabled={busy} onClick={() => onMarkPaid(p)}>
                  Mark paid manually
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [busy],
  );

  return (
    <div className="card flex flex-col gap-3 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-ink">Driver payouts</h3>
          <p className="text-xs text-ink-faint">
            Approve a request to send the money to the driver&apos;s UPI ID. &quot;Mark paid manually&quot; is only for
            transfers you made outside the system.
          </p>
        </div>
        <Tabs items={TABS} value={status} onChange={(v) => setStatus(v as PayoutStatus)} />
      </div>
      <DataTable
        columns={columns}
        data={payouts.data?.items ?? []}
        isLoading={payouts.isLoading}
        error={payouts.error}
        onRetry={() => payouts.refetch()}
        rowKey={(p) => p.id}
        emptyTitle="No payouts in this state"
      />
    </div>
  );
}
